import { Td, Tr, Button } from '@chakra-ui/react';
import { Text } from '~/common/components/defaultUIComponents';
import useTranslate from '~/common/utils/translate';
import type { ProjectRecordDto } from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import React, { useState } from 'react';

interface Props {
  record: ProjectRecordDto;
  onMarkSeen: (id: number) => void;
  isMarking: boolean;
  employeeName?: string;
}

const factorColor = (v: number | string | null): string => {
  if (v == null) return 'inherit';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return 'inherit';
  return n <= 2.5 ? 'green.500' : 'red.500';
};

const fmtFactor = (v: number | string | null): string => {
  if (v == null) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}x`;
};

const fmtDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtDate = (ts: number | null | undefined): string => {
  if (ts == null) return '—';
  // TimestampField returns milliseconds already
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

export const ProjectRecordListItem: React.FC<Props> = ({
  record,
  onMarkSeen,
  isMarking,
  employeeName: employeeNameProp,
}) => {
  const t = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const comment = record.comment ?? '';
  const employeeName = employeeNameProp ?? String(record.employee?.id ?? '');

  return (
    <Tr opacity={record.seen ? 0.5 : 1}>
      <Td>{record.project?.name ?? '—'}</Td>
      <Td>{employeeName}</Td>
      <Td>{fmtDate(record.start)}</Td>
      <Td>{fmtDuration(record.duration)}</Td>
      <Td
        maxW="240px"
        cursor={comment.length > 40 ? 'pointer' : 'default'}
        onClick={() => comment.length > 40 && setExpanded((v) => !v)}
      >
        <Text noOfLines={expanded ? undefined : 1}>{comment || '—'}</Text>
      </Td>
      <Td color={factorColor(record.factor_calculated)}>
        {fmtFactor(record.factor_calculated)}
      </Td>
      <Td color={factorColor(record.factor_aggregated)}>
        {fmtFactor(record.factor_aggregated)}
      </Td>
      <Td>
        {record.seen ? (
          '✓'
        ) : (
          <Button
            size="sm"
            onClick={() => onMarkSeen(record.id)}
            isLoading={isMarking}
          >
            {t('projectRecords.markSeen')}
          </Button>
        )}
      </Td>
    </Tr>
  );
};
